import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Link2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionCard } from "@/components/common/SectionCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useUser } from "@/context/UserContext";
import { api } from "@/lib/api";

export default function Links() {
  const { canWrite } = useUser();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, title, url } | null
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => {
    setLoading(true);
    api.links().then(setLinks).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const title = (editing.title || "").trim();
    const url = (editing.url || "").trim();
    if (!title || !url) { toast.error("Title and URL are required"); return; }
    try {
      if (editing.id) { await api.updateLink(editing.id, { title, url }); toast.success("Link updated"); }
      else { await api.createLink({ title, url }); toast.success("Link added"); }
      setEditing(null);
      load();
    } catch { /* interceptor surfaces errors */ }
  };

  const remove = async () => {
    try { await api.deleteLink(confirmDel.id); toast.success("Link deleted"); setConfirmDel(null); load(); } catch { /* handled */ }
  };

  return (
    <div data-testid="links-page">
      <PageHeader
        eyebrow="War Room"
        title="Links"
        description="Handy websites and tools for the Edju Cat team, all in one place."
        actions={canWrite && (
          <Button data-testid="add-link-button" onClick={() => setEditing({ title: "", url: "" })} className="rounded-full text-white [background-color:var(--accent-primary)] hover:opacity-90">
            <Plus size={16} /> Add Link
          </Button>
        )}
      />

      <SectionCard testid="links-card" accent="var(--accent-primary)" icon={Link2} title="Saved links" subtitle={`${links.length} link${links.length === 1 ? "" : "s"}`}>
        {loading ? (
          <p className="py-8 text-center text-sm text-[#9DAAC6]">Loading…</p>
        ) : links.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="links-empty">
            <Link2 size={26} className="text-[#C7D2E8]" />
            <p className="text-sm font-semibold text-[#14245F]">No links yet</p>
            <p className="max-w-sm text-xs text-[#5A6B8C]">{canWrite ? "Add a useful website with the “Add Link” button above." : "No links have been saved yet."}</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#EEF2FB]" data-testid="links-list">
            {links.map((l) => (
              <li key={l.id} data-testid={`link-row-${l.id}`} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`link-title-${l.id}`} className="block truncate text-sm font-semibold text-[#14245F] transition-colors hover:text-[var(--accent-primary)] hover:underline">{l.title}</a>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`link-url-${l.id}`} className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#5A6B8C] hover:underline"><ExternalLink size={11} className="shrink-0" /> <span className="truncate">{l.url}</span></a>
                </div>
                {canWrite && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="icon" variant="ghost" data-testid={`link-edit-${l.id}`} onClick={() => setEditing({ id: l.id, title: l.title, url: l.url })} className="h-8 w-8 rounded-full text-[#1D4ED8]" title="Edit"><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" data-testid={`link-delete-${l.id}`} onClick={() => setConfirmDel(l)} className="h-8 w-8 rounded-full text-[#C2410C]" title="Delete"><Trash2 size={14} /></Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="link-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit link" : "Add link"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="link-title">Title</Label>
              <Input id="link-title" data-testid="link-title-input" value={editing?.title || ""} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} placeholder="Edju Cat website" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-url">URL</Label>
              <Input id="link-url" data-testid="link-url-input" value={editing?.url || ""} onChange={(e) => setEditing((s) => ({ ...s, url: e.target.value }))} placeholder="https://edjucat.com" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="link-save-button" onClick={save} className="text-white [background-color:var(--accent-primary)] hover:opacity-90">{editing?.id ? "Save" : "Add link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)} title="Delete this link?" softDelete onConfirm={remove} />
    </div>
  );
}
